import { formatPrice } from '@polaris/core/utils'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import type { TrendingAsset } from '@/src/services/tokens'
import { cn } from '@/src/utils/styles'
import { PriceDisplay } from '../price-display'
import { CardContent } from '../ui/card'
import { PriceChange } from '../ui/price-change'
import TokenImg from '../ui/token-img'

export function TrendingCard({ token }: { token: TrendingAsset }) {
  const router = useRouter()
  const { ampli } = useAmplitudeAnalytics()

  const ticker = encodeURIComponent(token.ticker)
  const searchParams = new URLSearchParams({
    ticker: ticker,
    toToken: token.denom,
    toChain: token.chain_id,
  }).toString()
  const url = `/tokens?${searchParams}`

  return (
    <Link
      href={url}
      prefetch={true}
      onClick={() => {
        ampli?.pageInteraction({
          page: 'home',
          key: 'click on trending token card',
          context: token.ticker,
        })
      }}
      onMouseEnter={() => {
        router.prefetch(url)
      }}
      className='min-w-[15rem] max-w-[19.5rem] focus-outer block rounded-2xl'
    >
      <div className='p-3 flex flex-col gap-6 bg-foreground rounded-2xl border-none shadow-sm transition-transform duration-300 ease-out hover:scale-[1.02]'>
        <div className='p-0 flex flex-row items-center justify-between gap-3'>
          <div className='flex items-center gap-3 min-w-0'>
            <TokenImg
              src={token.kind_logo_img_url}
              width={32}
              height={32}
              className='shrink-0'
              unoptimized
            />
            <div className='flex flex-col min-w-0'>
              <h3
                className={cn('text-sm font-semibold leading-6 truncate', {
                  'text-tertiary': !token.kind_display_name,
                  'text-primary': token.kind_display_name,
                })}
              >
                {token.kind_display_name || 'Unknown name'}
              </h3>
              <div className='flex flex-row items-center gap-1'>
                <span className='text-secondary text-sm leading-6 truncate'>
                  {token.ticker}
                </span>
              </div>
            </div>
          </div>
          <div className='flex flex-col items-end shrink-0'>
            <span className='text-base text-right text-primary'>
              <PriceDisplay price={token.price_usd} />
            </span>
            <PriceChange change={token.price_change_percentage_24h} />
          </div>
        </div>
        <CardContent className='flex flex-row gap-3 p-0'>
          <div className='flex flex-col items-start grow'>
            <span className='text-3xl text-primary font-serif font-extralight leading-9'>
              {formatPrice(token.total_volume, {
                notation: 'compact',
                maximumSignificantDigits: 3,
                roundingPriority: 'auto',
              })}
            </span>
            <p className='text-sm text-secondary leading-5'>Volume (24h)</p>
          </div>
          <div className='flex flex-col items-start grow'>
            <span className='text-3xl text-primary font-serif font-extralight leading-9'>
              {formatPrice(token.market_cap, {
                notation: 'compact',
                maximumSignificantDigits: 3,
                roundingPriority: 'auto',
              })}
            </span>
            <p className='text-sm text-secondary leading-5'>Market cap</p>
          </div>
        </CardContent>
      </div>
    </Link>
  )
}
